import { useEffect, useState } from "react";
import { getUsage, type Usage } from "./usageApi";

/**
 * THE one shared hook behind the pill, the Home tile and the in-flow line (§6) — and, since the
 * lead's 2026-10-02 course correction, ONE shared fetch behind all three, not three independent
 * round trips that happen to parse the response the same way.
 *
 * WHY A MODULE-LEVEL STORE, AND NOT A CONTEXT PROVIDER. The owner's spec is "A, B and C all read
 * the same `GET /api/me/usage`" — one source, so they can never disagree. Three components each
 * calling their own `fetch` cannot keep that promise: the account's usage can change BETWEEN two
 * independent mounts (the ordinary case — the pill lives in the persistent `AppShell` and the
 * tile/line mount fresh on every page nav, including right after a Generate that just changed the
 * count), so two surfaces on screen at once could legitimately show two different numbers for the
 * same account at nearly the same moment. A plain module-level cache with a subscriber set fixes
 * that without requiring every caller to sit under a new provider — `UsagePill`/`UsageTile`/
 * `UsageLine` can go on mounting anywhere in the tree, including as unrelated siblings with no
 * common ancestor besides the app root, which is exactly how the three are actually used.
 *
 * ONE FETCH, DEDUPED SYNCHRONOUSLY. `status` flips to `"loading"` the instant the first caller
 * asks, before `getUsage()`'s promise has had a chance to settle — so every OTHER component that
 * mounts in the same tick (or while the first request is still in flight) sees `"loading"` and
 * just subscribes, rather than firing a second request. All subscribers are notified together
 * when the one request resolves, so they re-render with the identical value in the same tick.
 *
 * `usage` stays `null` on any failure — not signed in, the route 404ing on an older server, the
 * network being down — and every caller treats that the same way `ServiceNotice` treats a failed
 * probe: we learned nothing, so we show nothing, rather than a zero that looks like a real count.
 */
export interface UsageState {
  usage: Usage | null;
  loading: boolean;
  failed: boolean;
  /** Re-run the round trip for EVERY mounted surface at once — used after an event that can
   * change the count (a Generate accepted or refused, an admin changing this account's limit) so
   * the pill, the tile and the in-flow line update together rather than drifting until their next
   * independent remount. */
  refresh: () => void;
}

type Status = "idle" | "loading" | "loaded" | "failed";

let cache: Usage | null = null;
let status: Status = "idle";
let pending: Promise<void> | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

function load(): void {
  // Already in flight or already answered: every other subscriber just reads `cache`/`status`
  // off the single in-flight (or already-settled) request via `notify()` below.
  if (pending) return;
  status = "loading";
  pending = getUsage()
    .then((u) => {
      cache = u;
      status = "loaded";
    })
    .catch(() => {
      cache = null;
      status = "failed";
    })
    .finally(() => {
      pending = null;
      notify();
    });
}

/** Forces every mounted `useUsage()` caller to re-fetch and re-render together. Exported for
 * callers outside a render — e.g. the wizard, once a Generate is accepted or comes back
 * `limit_reached`, so the count other open surfaces are showing does not go stale. */
export function refreshUsage(): void {
  status = "idle";
  cache = null;
  load();
  notify(); // flips every mounted surface to "loading" immediately, not only once it resolves
}

/** Test-only escape hatch: the module-level cache otherwise survives across tests in the same
 * worker, which would leak one test's account into the next. */
export function __resetUsageCacheForTests(): void {
  cache = null;
  status = "idle";
  pending = null;
}

export function useUsage(): UsageState {
  const [, setTick] = useState(0);

  useEffect(() => {
    const listener = () => setTick((t) => t + 1);
    listeners.add(listener);
    if (status === "idle") load();
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return {
    usage: cache,
    loading: status === "idle" || status === "loading",
    failed: status === "failed",
    refresh: refreshUsage,
  };
}
